/**
 * FiltersService - Сервис для работы с фильтрами
 * ===============================================
 * 
 * Преобразование фильтров между форматами, валидация и утилиты
 * 
 * ВАЖНО: Работа с датами
 * - Пользователь выбирает СЕРВЕРНОЕ время в интерфейсе
 * - Даты передаются на сервер в формате YYYY-MM-DD (без timezone)
 * - Сервер интерпретирует даты как локальное (серверное) время
 */

export class FiltersService {
  /**
   * Преобразует фильтры из контекста в query параметры для API
   */
  static toQueryParams(filters) {
    const params = {};
    
    // Обработка дат
    // ВАЖНО: Используем formatDate который извлекает локальные компоненты даты
    // Это гарантирует что выбранная пользователем дата не сдвинется из-за timezone
    if (filters.dateRange) {
      if (filters.dateRange.startDate) {
        params.startDate = this.formatDate(filters.dateRange.startDate);
      }
      if (filters.dateRange.endDate) {
        params.endDate = this.formatDate(filters.dateRange.endDate);
      }
    }
    
    // Для совместимости со старым API (где используются отдельные поля)
    if (filters.startDate) {
      params.startDate = this.formatDate(filters.startDate);
    }
    if (filters.endDate) {
      params.endDate = this.formatDate(filters.endDate);
    }
    
    // Проект
    if (filters.project && filters.project !== 'all') {
      params.project = filters.project;
    }
    if (filters.selectedProject && filters.selectedProject !== 'all') {
      params.project = filters.selectedProject;
    }
    
    // Источник трафика
    if (filters.mediaSource && filters.mediaSource !== 'all') {
      params.mediaSource = filters.mediaSource;
    }
    if (filters.selectedMediaSource && filters.selectedMediaSource !== 'all') {
      params.mediaSource = filters.selectedMediaSource;
    }
    
    // Кампании
    if (filters.campaigns && filters.campaigns.length > 0) {
      params.campaigns = filters.campaigns;
    }
    if (filters.selectedCampaigns && filters.selectedCampaigns.length > 0) {
      params.campaigns = filters.selectedCampaigns;
    }
    
    // Платформа
    if (filters.platform && filters.platform !== 'all') {
      params.platform = filters.platform;
    }
    if (filters.selectedPlatform && filters.selectedPlatform !== 'all') {
      params.platform = filters.selectedPlatform;
    }
    
    // Версии клиента / установки (массивы ApiService разворачивает в повторяющиеся ключи)
    if (Array.isArray(filters.clientVersions) && filters.clientVersions.length > 0) {
      params.clientVersions = filters.clientVersions;
    }
    if (Array.isArray(filters.installVersions) && filters.installVersions.length > 0) {
      params.installVersions = filters.installVersions;
    }

    // Гео (коды стран). Не глобальный фильтр сайдбара, а локальный параметр
    // раздела: виджет подмешивает его в filters сам (см. useRetentionGeo).
    // Учитывают его только эндпоинты ретеншена — остальные игнорируют.
    if (Array.isArray(filters.geo) && filters.geo.length > 0) {
      params.geo = filters.geo;
    }

    // Дополнительные параметры
    if (filters.groupBy) {
      params.groupBy = filters.groupBy;
    }
    
    // Тип фильтрации по датам (когортная/календарная)
    if (filters.dateFilterType) {
      params.dateFilterType = filters.dateFilterType;
    }
    
    return params;
  }
  
  /**
   * Форматирует дату в строку YYYY-MM-DD
   * 
   * ВАЖНО: Используем ЛОКАЛЬНЫЕ методы даты (getFullYear, getMonth, getDate)
   * а не UTC методы. Это гарантирует что дата которую выбрал пользователь
   * в календаре будет передана на сервер без сдвига из-за timezone.
   * 
   * Пример: Пользователь выбрал 25 ноября в календаре
   * - Локальное время: 2025-11-25 00:00:00 (UTC+2)
   * - Если использовать toISOString(): 2025-11-24T22:00:00.000Z (сдвиг на день назад!)
   * - Если использовать локальные методы: 2025-11-25 (правильно!)
   */
  static formatDate(date) {
    if (!date) return null;
    
    // Если уже строка в нужном формате
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    
    // Преобразуем в Date если нужно
    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      console.warn('Invalid date:', date);
      return null;
    }
    
    // ВАЖНО: Используем локальные методы, НЕ UTC!
    // Это гарантирует что выбранная дата не сдвинется из-за timezone
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }
  
  /**
   * Проверяет наличие активных фильтров
   */
  static hasActiveFilters(filters) {
    return !!(
      filters.dateRange?.startDate ||
      filters.dateRange?.endDate ||
      filters.startDate ||
      filters.endDate ||
      (filters.project && filters.project !== 'all') ||
      (filters.selectedProject && filters.selectedProject !== 'all') ||
      (filters.mediaSource && filters.mediaSource !== 'all') ||
      (filters.selectedMediaSource && filters.selectedMediaSource !== 'all') ||
      (filters.campaigns && filters.campaigns.length > 0) ||
      (filters.selectedCampaigns && filters.selectedCampaigns.length > 0) ||
      (filters.clientVersions && filters.clientVersions.length > 0) ||
      (filters.installVersions && filters.installVersions.length > 0)
    );
  }
  
  /**
   * Создает фильтры для последних N дней
   */
  static createLastNDaysFilter(days) {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    return {
      dateRange: {
        startDate,
        endDate
      }
    };
  }
  
  /**
   * Создает фильтры для текущего месяца
   */
  static createCurrentMonthFilter() {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return {
      dateRange: {
        startDate,
        endDate
      }
    };
  }
  
  /**
   * Создает фильтры для предыдущего месяца
   */
  static createPreviousMonthFilter() {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endDate = new Date(now.getFullYear(), now.getMonth(), 0);
    
    return {
      dateRange: {
        startDate,
        endDate
      }
    };
  }
  
  /**
   * Создает фильтры для диапазона дат
   */
  static createDateRangeFilter(startDate, endDate) {
    return {
      dateRange: {
        startDate: new Date(startDate),
        endDate: new Date(endDate)
      }
    };
  }
  
  /**
   * Объединяет несколько объектов фильтров
   */
  static mergeFilters(...filterObjects) {
    const merged = {};
    
    for (const filters of filterObjects) {
      if (!filters) continue;
      
      // Даты
      if (filters.dateRange) {
        merged.dateRange = filters.dateRange;
      }
      if (filters.startDate || filters.endDate) {
        merged.dateRange = {
          startDate: filters.startDate,
          endDate: filters.endDate
        };
      }
      
      // Проект
      if (filters.project) {
        merged.project = filters.project;
      }
      if (filters.selectedProject) {
        merged.project = filters.selectedProject;
      }
      
      // Источник
      if (filters.mediaSource) {
        merged.mediaSource = filters.mediaSource;
      }
      if (filters.selectedMediaSource) {
        merged.mediaSource = filters.selectedMediaSource;
      }
      
      // Кампании
      if (filters.campaigns) {
        merged.campaigns = filters.campaigns;
      }
      if (filters.selectedCampaigns) {
        merged.campaigns = filters.selectedCampaigns;
      }
    }
    
    return merged;
  }
  
  /**
   * Валидирует фильтры
   */
  static validateFilters(filters) {
    const errors = [];
    
    // Проверка дат
    if (filters.dateRange) {
      const { startDate, endDate } = filters.dateRange;
      
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (start > end) {
          errors.push('Начальная дата не может быть позже конечной');
        }
        
        // Проверка на слишком большой диапазон (например, больше года)
        const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        if (daysDiff > 365) {
          errors.push('Диапазон дат не должен превышать 365 дней');
        }
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
  
  /**
   * Создает строку описания фильтров для отображения
   */
  static getFilterDescription(filters) {
    const parts = [];
    
    // Даты
    if (filters.dateRange?.startDate && filters.dateRange?.endDate) {
      const start = this.formatDate(filters.dateRange.startDate);
      const end = this.formatDate(filters.dateRange.endDate);
      parts.push(`${start} - ${end}`);
    }
    
    // Проект
    const project = filters.project || filters.selectedProject;
    if (project && project !== 'all') {
      parts.push(`Project: ${project}`);
    }
    
    // Источник
    const source = filters.mediaSource || filters.selectedMediaSource;
    if (source && source !== 'all') {
      parts.push(`Source: ${source}`);
    }
    
    // Кампании
    const campaigns = filters.campaigns || filters.selectedCampaigns;
    if (campaigns && campaigns.length > 0) {
      parts.push(`Campaigns: ${campaigns.length}`);
    }
    
    return parts.length > 0 ? parts.join(' | ') : 'No filters';
  }
}

// Экспортируем удобные функции
export const toQueryParams = (filters) => FiltersService.toQueryParams(filters);
export const hasActiveFilters = (filters) => FiltersService.hasActiveFilters(filters);
export const formatDate = (date) => FiltersService.formatDate(date);
